/**
 * Already-ordered lookups derived from a week's submitted family orders.
 *
 * Drives the "N ordered" subtext under each table cell and the per-child
 * ordered-item thumbnails on a collapsed day card. Pure — built once from the
 * `FamilyOrderWire` rows (see `@/api/orders-family`), then queried by the
 * components. The column axis is the **child** (`studentUserId`).
 */

import type { FamilyOrderWire } from '@/api/family-orders'
import type { Mealtime } from '@/api/school-orders'

/** An inactive (cancelled/refunded) line summed for one receipt row. */
export interface InactiveOrderLine {
  state: 'cancelled' | 'refunded'
  quantity: number
}

/**
 * Cancelled/refunded lines for ONE day at ONE campus, keyed by
 * (mealtime, child, menu item) — the receipt view renders these as muted
 * rows with a "Cancelled" / "Refunded" badge instead of dropping them.
 * When a cell holds both kinds, `refunded` wins (money moved back is the
 * fact a parent checks for).
 */
export function buildFamilyInactiveLines(
  orders: ReadonlyArray<FamilyOrderWire>,
  scope: { dateKey: string; serviceLocationId: string },
): (mealtime: Mealtime, studentUserId: string, menuItemId: string) => InactiveOrderLine | null {
  const byCell = new Map<string, InactiveOrderLine>()
  for (const order of orders) {
    if (order.serviceDate.slice(0, 10) !== scope.dateKey || order.serviceLocationId !== scope.serviceLocationId) {
      continue
    }
    for (const item of order.items) {
      const state = item.lineState
      if ((state !== 'cancelled' && state !== 'refunded') || !item.studentUserId) {
        continue
      }
      const key = cellKey(scope.dateKey, order.mealtime, item.studentUserId, item.menuItemId)
      const current = byCell.get(key)
      byCell.set(key, {
        state: current?.state === 'refunded' || state === 'refunded' ? 'refunded' : 'cancelled',
        quantity: (current?.quantity ?? 0) + item.quantity,
      })
    }
  }
  return (mealtime, studentUserId, menuItemId) =>
    byCell.get(cellKey(scope.dateKey, mealtime, studentUserId, menuItemId)) ?? null
}

function cellKey(dateKey: string, mealtime: Mealtime, studentUserId: string, menuItemId: string): string {
  return `${dateKey}::${mealtime}::${studentUserId}::${menuItemId}`
}

function mealtimeStudentKey(dateKey: string, mealtime: Mealtime, studentUserId: string): string {
  return `${dateKey}::${mealtime}::${studentUserId}`
}

function dayStudentKey(dateKey: string, studentUserId: string): string {
  return `${dateKey}::${studentUserId}`
}

/** A submitted menu item for a child on a day — the collapsed-card thumbnail. */
export interface OrderedItemSummary {
  menuItemId: string
  title: string
  imageUri: string | null
  quantity: number
}

export interface FamilyAlreadyOrdered {
  /** Quantity already ordered for a specific (date, mealtime, child, item). */
  cellQty(dateKey: string, mealtime: Mealtime, studentUserId: string, menuItemId: string): number
  /** Quantity already ordered across all items for a (date, mealtime, child). */
  mealtimeStudentQty(dateKey: string, mealtime: Mealtime, studentUserId: string): number
  /**
   * The distinct items a child has ordered on a day, summed across mealtimes,
   * in first-seen order — the collapsed day-card thumbnail row.
   */
  itemsForChildOnDay(dateKey: string, studentUserId: string): OrderedItemSummary[]
  /**
   * The distinct items a child has ordered on a day, grouped by mealtime
   * (first-seen order within each) — drives the per-mealtime thumbnail groups.
   */
  itemsForChildOnDayByMealtime(dateKey: string, studentUserId: string): Map<Mealtime, OrderedItemSummary[]>
  /**
   * True when the child's (day, mealtime) holds at least one AUTO-placed
   * order line (subscription-stamped batch). Distinguishes "the auto-order
   * ran" from "the parent hand-ordered and the auto-order skipped" — the
   * PROD-1037 caption keys on the latter.
   */
  hasAutoOrder(dateKey: string, mealtime: Mealtime, studentUserId: string): boolean
}

/**
 * Sum submitted family-order item quantities into per-cell, per-(mealtime,
 * child), and per-(day, child) lookups. Items with a null `studentUserId` are
 * ignored (can't be attributed to a column).
 */
export function buildFamilyAlreadyOrdered(orders: ReadonlyArray<FamilyOrderWire>): FamilyAlreadyOrdered {
  const byCell = new Map<string, number>()
  const byMealtimeStudent = new Map<string, number>()
  const autoOrderedSlots = new Set<string>()
  const byDayStudent = new Map<string, Map<string, OrderedItemSummary>>()
  const byDayStudentMealtime = new Map<string, Map<Mealtime, Map<string, OrderedItemSummary>>>()
  for (const order of orders) {
    // Cancelled/refunded batches are not "already ordered" — they must not
    // render as ordered items or consume per-day/per-mealtime counts. The
    // server excludes VOIDED batches from listWeek (status guard kept for
    // stale caches) and projects the derived `state` for refunded /
    // all-lines-dead batches. (Lines removed from a LIVE batch are archived
    // server-side and never projected.)
    if (order.status === 'VOIDED' || (order.state ?? 'active') !== 'active') {
      continue
    }
    const dateKey = order.serviceDate.slice(0, 10)
    for (const item of order.items) {
      if (!item.studentUserId) {
        continue
      }
      // A cancelled or refunded line inside a live batch is not "already
      // ordered" either — it must not seed edit quantities, count toward
      // per-day/-mealtime sums, or show as an ordered thumbnail. The receipt
      // view renders these muted with a badge from the order payload directly.
      if ((item.lineState ?? 'active') !== 'active') {
        continue
      }
      const ck = cellKey(dateKey, order.mealtime, item.studentUserId, item.menuItemId)
      byCell.set(ck, (byCell.get(ck) ?? 0) + item.quantity)
      const mk = mealtimeStudentKey(dateKey, order.mealtime, item.studentUserId)
      byMealtimeStudent.set(mk, (byMealtimeStudent.get(mk) ?? 0) + item.quantity)
      if (order.isAutoOrder === true) {
        autoOrderedSlots.add(mk)
      }

      const dk = dayStudentKey(dateKey, item.studentUserId)
      const itemsForDay = byDayStudent.get(dk) ?? new Map<string, OrderedItemSummary>()
      const existing = itemsForDay.get(item.menuItemId)
      if (existing) {
        existing.quantity += item.quantity
      } else {
        itemsForDay.set(item.menuItemId, {
          menuItemId: item.menuItemId,
          title: item.title,
          imageUri: item.imageUri,
          quantity: item.quantity,
        })
      }
      byDayStudent.set(dk, itemsForDay)

      const byMealtime = byDayStudentMealtime.get(dk) ?? new Map<Mealtime, Map<string, OrderedItemSummary>>()
      const itemsForMealtime = byMealtime.get(order.mealtime) ?? new Map<string, OrderedItemSummary>()
      const existingForMealtime = itemsForMealtime.get(item.menuItemId)
      if (existingForMealtime) {
        existingForMealtime.quantity += item.quantity
      } else {
        itemsForMealtime.set(item.menuItemId, {
          menuItemId: item.menuItemId,
          title: item.title,
          imageUri: item.imageUri,
          quantity: item.quantity,
        })
      }
      byMealtime.set(order.mealtime, itemsForMealtime)
      byDayStudentMealtime.set(dk, byMealtime)
    }
  }
  return {
    cellQty: (dateKey, mealtime, studentUserId, menuItemId) =>
      byCell.get(cellKey(dateKey, mealtime, studentUserId, menuItemId)) ?? 0,
    mealtimeStudentQty: (dateKey, mealtime, studentUserId) =>
      byMealtimeStudent.get(mealtimeStudentKey(dateKey, mealtime, studentUserId)) ?? 0,
    itemsForChildOnDay: (dateKey, studentUserId) => [
      ...(byDayStudent.get(dayStudentKey(dateKey, studentUserId))?.values() ?? []),
    ],
    itemsForChildOnDayByMealtime: (dateKey, studentUserId) => {
      const byMealtime = byDayStudentMealtime.get(dayStudentKey(dateKey, studentUserId))
      const out = new Map<Mealtime, OrderedItemSummary[]>()
      for (const [mealtime, items] of byMealtime ?? []) {
        out.set(mealtime, [...items.values()])
      }
      return out
    },
    hasAutoOrder: (dateKey, mealtime, studentUserId) =>
      autoOrderedSlots.has(mealtimeStudentKey(dateKey, mealtime, studentUserId)),
  }
}
